<div class="min-h-screen flex items-center justify-center px-4 py-10">
  <div *ngIf="loading" class="flex items-center justify-center bg-slate-50 px-4">
    <div
      class="rounded-2xl shadow-lg border border-slate-200 px-8 py-10 text-center max-w-md w-full"
    >
      <div
        class="mx-auto mb-5 h-12 w-12 rounded-full border-4 border-slate-200 border-t-[#1a3a52] animate-spin"
      ></div>

      <h2 class="text-xl font-bold text-slate-800">Authenticating Certificate</h2>

      <p class="mt-2 text-sm text-slate-500">
        Please wait while we validate the certificate details against official records.
      </p>
    </div>
  </div>
  <div class="w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden" *ngIf="!loading">
    <div
      class="text-white text-center py-8 px-4"
      [ngClass]="status === true ? 'bg-[#1a3a52]' : 'bg-red-800'"
    >
      <ng-container *ngIf="status === true && type; else verificationRequired">
        <h1 class="text-2xl md:text-3xl text-white font-bold tracking-wide">
          Certificate Successfully Verified
        </h1>

        <p class="text-sm md:text-base opacity-90 mt-2">
          Official
          <ng-container [ngSwitch]="type">
            <span *ngSwitchCase="'TIN'">Company Registration</span>
            <span *ngSwitchCase="'RESERVATION'">Name Reservation</span>
            <span *ngSwitchCase="'LICENSE'">Business Licence</span>
          </ng-container>
          verification record authenticated by VFIFC
        </p>
      </ng-container>

      <ng-template #verificationRequired>
        <div class="max-w-3xl mx-auto">
          <div
            class="inline-flex items-center justify-center w-14 h-14 rounded-full bg-white text-red-700 mb-4"
          >
            <span class="text-3xl font-bold">!</span>
          </div>

          <h1 class="text-2xl md:text-3xl text-white font-bold tracking-wide">
            Certificate verification failed
          </h1>

          <p class="mt-3 text-sm md:text-base text-red-100">
            Please verify your certificate details or contact VFIFC support for assistance.
          </p>

          <div class="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl mx-auto">
            <a
              href="tel:+2638612121712"
              class="block rounded-lg bg-white text-red-800 px-4 py-3 font-semibold shadow-md hover:bg-red-50 transition"
            >
              <span class="block text-xs uppercase tracking-wide text-red-500"> Phone </span>
              +263 8612 121 712
            </a>

            <a
              href="mailto:info@vfifc.com"
              class="block rounded-lg bg-white text-red-800 px-4 py-3 font-semibold shadow-md hover:bg-red-50 transition"
            >
              <span class="block text-xs uppercase tracking-wide text-red-500"> Email </span>
              info&#64;vfifc.com
            </a>
          </div>

          <p
            class="mt-5 inline-block rounded-md bg-red-950/40 px-4 py-2 text-sm text-red-50"
            *ngIf="error"
          >
            {{ error }}
          </p>
        </div>
      </ng-template>
    </div>

    <div class="p-6" *ngIf="type && status">
      <div *ngIf="loading" class="flex flex-col items-center justify-center py-10">
        <div
          class="animate-spin rounded-full h-10 w-10 border-4 border-[#1a3a52] border-t-transparent"
        ></div>
        <p class="mt-4 text-[#1a3a52] font-medium">Validating certificate, please wait...</p>
      </div>
      <div
        *ngIf="error"
        class="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg text-center"
      >
        <p class="font-semibold">❌ Validation Failed</p>
        <p class="mt-1 text-sm">{{ error }}</p>
      </div>

      <!-- COMPANY VERIFIED -->
      <div *ngIf="companyData && type === 'TIN'" class="space-y-6">
        <div
          class="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center justify-between"
        >
          <div>
            <p class="text-green-700 font-bold text-lg">✅ Company Verified</p>
            <p class="text-green-600 text-sm">This company is officially registered and valid</p>
          </div>
          <span class="px-4 py-1 rounded-full bg-green-600 text-white text-sm font-semibold">
            LEGIT
          </span>
        </div>
        <div class="border border-gray-200 rounded-xl p-5 space-y-4">
          <div>
            <p class="text-sm text-gray-500">Company Name</p>
            <p class="text-lg font-semibold text-[#1a3a52]">{{ companyData.companyName }}</p>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p class="text-sm text-gray-500">TIN Number</p>
              <p class="font-medium text-gray-800">{{ companyData.tinNumber }}</p>
            </div>

            <div>
              <p class="text-sm text-gray-500">Category</p>
              <p class="font-medium text-gray-800">{{ companyData.companyCategory || 'N/A' }}</p>
            </div>

            <div>
              <p class="text-sm text-gray-500">Status</p>
              <p class="font-medium text-green-600">{{ companyData.companyStatus }}</p>
            </div>

            <div>
              <p class="text-sm text-gray-500">Country of Incorporation</p>
              <p class="font-medium text-gray-800">
                {{ companyData.countryOfIncorporation || 'N/A' }}
              </p>
            </div>
          </div>
        </div>

        <div class="text-center pt-4">
          <p class="text-xs text-gray-500">
            This certificate is authentic and verified by the registry system.
          </p>
        </div>
      </div>

      <!-- NAME RESERVATION VERIFIED -->
      <div *ngIf="reservationData && type === 'RESERVATION'" class="space-y-6">
        <div
          class="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between"
        >
          <div>
            <p class="text-blue-700 font-bold text-lg">✅ Name Reservation Verified</p>
            <p class="text-blue-600 text-sm">This business name is officially reserved</p>
          </div>
          <span class="px-4 py-1 rounded-full bg-blue-600 text-white text-sm font-semibold">
            LEGIT
          </span>
        </div>

        <div class="border border-gray-200 rounded-xl p-5 space-y-4">
          <div>
            <p class="text-sm text-gray-500">Reserved Company Name</p>
            <p class="text-lg font-semibold text-[#1a3a52]">{{ reservationData.companyName }}</p>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p class="text-sm text-gray-500">Reservation ID</p>
              <p class="font-medium text-gray-800">{{ reservationData.nameReservationId }}</p>
            </div>

            <div>
              <p class="text-sm text-gray-500">Expiry Date</p>
              <p class="font-medium text-gray-800">{{ reservationData.expiryDate | date }}</p>
            </div>
          </div>
        </div>
        <div class="text-center pt-4">
          <p class="text-xs text-gray-500">
            This reservation is authentic and issued by the registry system.
          </p>
          <button
            class="px-4 py-2 mt-4 bg-[#1a3a52] text-white rounded-lg hover:bg-[#1a3a52] transition-colors duration-300"
            (click)="goToWelcomePage()"
          >
            Visit Our Website to Register Your Business
          </button>
        </div>
      </div>

      <!-- LICENSE VERIFIED -->
      <div *ngIf="licenseData && type === 'LICENSE'" class="space-y-6">
        <div
          class="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center justify-between"
        >
          <div>
            <p class="text-green-700 font-bold text-lg">Licence Verified</p>
            <p class="text-green-600 text-sm">This licence is officially registered and valid.</p>
          </div>
          <span class="px-4 py-1 rounded-full bg-green-600 text-white text-sm font-semibold">
            LEGIT
          </span>
        </div>
        <div class="border border-gray-200 rounded-xl p-5 space-y-4">
          <p class="text-sm text-gray-500">Licence Type</p>
          <p class="font-medium text-gray-800">{{ (licenseData.licenseType | removeUnderscore) || 'N/A' }}</p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p class="text-sm text-gray-500">Licence Number</p>
            <p class="text-lg font-semibold text-[#1a3a52]">{{ licenseData.licenseNumber }}</p>
          </div>
          <div>
            <p class="text-sm text-gray-500">Company Name</p>
            <p class="text-lg font-semibold text-[#1a3a52]">{{ licenseData.companyName }}</p>
          </div>
          <div>
            <p class="text-sm text-gray-500">TIN Number</p>
            <p class="font-medium text-gray-800">{{ licenseData.tinNumber || 'N/A' }}</p>
          </div>

          <div>
            <div>
              <p class="text-sm text-gray-500">Status</p>
              <p class="font-medium text-green-600">{{ licenseData.status }}</p>
            </div>
          </div>
        </div>

        <div class="text-center pt-4">
          <p class="text-xs text-gray-500">
            This certificate is authentic and verified by the registry system.
          </p>
        </div>
      </div>
    </div>
  </div>
</div>
